// 月次思想ジャーナル
import { escapeHtml, screenHead, showScreen, showLoading, hideLoading, toast, renderLongText } from '../ui.js';
import { generateJournal, fetchAllNotesParallel } from '../api.js';
import { getAllBooks, upsertBooks, saveJournal, getJournal, getAllJournals, saveAiHistory } from '../cache.js';

export async function showJournalScreen() {
  const books = await getAllBooks();
  const journals = await getAllJournals();
  renderPicker(books, journals);
}

async function ensureNotes(books) {
  const missing = books.filter((b) => !b.note).length;
  if (!missing) return;
  showLoading(`書籍メモを補完中… (0/${missing})`);
  await fetchAllNotesParallel(books, (done, total) => {
    const ov = document.getElementById('overlay-msg');
    if (ov) ov.textContent = `書籍メモを補完中… (${done}/${total})`;
  });
  await upsertBooks(books);
  hideLoading();
}

function renderPicker(books, journals) {
  // 利用可能な月を逆順
  const months = uniqueMonths(books);
  const html = `
    ${screenHead('月次思想ジャーナル')}
    <p style="color:var(--dim);font-size:13px;line-height:1.75;margin:0 0 24px;">
      指定した月の読書履歴とメモから、思考の変化と次月への問いを編みます。
    </p>
    <div class="form-group">
      <label class="form-label">月を選ぶ</label>
      <select class="form-select" id="month-select">
        ${months.map((m) => `<option value="${m}">${formatMonth(m)}</option>`).join('')}
      </select>
    </div>
    <button class="btn btn--ink btn--block" id="btn-go" type="button">
      <svg class="icon"><use href="#i-sparkles"/></svg>
      <span>ジャーナルを生成</span>
    </button>

    ${
      journals.length
        ? `
      <p class="section-label" style="margin-top:32px;">past entries</p>
      <ul style="list-style:none;padding:0;margin:0;">
        ${journals
          .sort((a, b) => b.yyyymm.localeCompare(a.yyyymm))
          .map(
            (j) => `
          <li class="book-item" data-yyyymm="${escapeHtml(j.yyyymm)}">
            <div class="book-body">
              <p class="book-title">${escapeHtml(formatMonth(j.yyyymm))}</p>
              <p class="book-author">${escapeHtml((j.content || '').slice(0, 80))}…</p>
            </div>
            <svg class="icon action__chevron"><use href="#i-arrow-right"/></svg>
          </li>
        `
          )
          .join('')}
      </ul>
    `
        : ''
    }
  `;
  const screen = showScreen('journal', html);
  screen.querySelector('[data-action="back"]').addEventListener('click', () => window.history.back());
  screen.querySelector('#btn-go').addEventListener('click', async () => {
    const yyyymm = screen.querySelector('#month-select').value;
    await runJournal(yyyymm, books);
  });
  screen.querySelectorAll('[data-yyyymm]').forEach((li) => {
    li.addEventListener('click', async () => {
      const yyyymm = li.dataset.yyyymm;
      const entry = await getJournal(yyyymm);
      if (entry) renderResult(yyyymm, entry.content);
    });
  });
}

function uniqueMonths(books) {
  const set = new Set();
  for (const b of books) {
    const d = b.endDate || b.startDate;
    if (d) set.add(d.slice(0, 7).replace('-', ''));
  }
  // 現在月を含める
  const now = new Date();
  set.add(`${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}`);
  return [...set].sort().reverse();
}

function formatMonth(yyyymm) {
  return `${yyyymm.slice(0, 4)} 年 ${yyyymm.slice(4)} 月`;
}

async function runJournal(yyyymm, allBooks) {
  // その月に Status=読了/読書中で関わった本を抽出
  const monthBooks = allBooks.filter((b) => {
    const dates = [b.startDate, b.endDate].filter(Boolean);
    return dates.some((d) => d.slice(0, 7).replace('-', '') === yyyymm);
  });
  if (!monthBooks.length) {
    toast('この月の読書記録が見つかりません');
    return;
  }
  await ensureNotes(monthBooks);
  showLoading('ジャーナルを編集中…');
  try {
    const { journal } = await generateJournal(yyyymm, monthBooks);
    await saveJournal(yyyymm, journal);
    await saveAiHistory({
      type: 'journal',
      title: `${yyyymm.slice(0, 4)} 年 ${yyyymm.slice(4)} 月のジャーナル`,
      subtitle: `${monthBooks.length} 冊の読書を編集`,
      context: { yyyymm },
      payload: { content: journal },
    });
    renderResult(yyyymm, journal);
  } catch (e) {
    toast('生成に失敗しました');
  } finally {
    hideLoading();
  }
}

export function renderFromHistory(entry) {
  renderResult(entry.context?.yyyymm || '', entry.payload?.content || '');
}

function renderResult(yyyymm, content) {
  const html = `
    ${screenHead(formatMonth(yyyymm))}
    <div class="long-text">${renderLongText(content)}</div>
  `;
  const screen = showScreen('journal-result', html);
  screen.querySelector('[data-action="back"]').addEventListener('click', () => window.history.back());
}
